/**
 * FontExtractor - Utility for extracting unique fonts from text runs
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility extracts unique font UIDs from text runs for font preloading.
 */

/**
 * Font request format for FontDownloadService
 */
export interface FontRequest {
  font_uid: string;
  fileId?: string;
}

/**
 * Extract unique fonts from text runs
 * 
 * Iterates through text runs and collects unique font UIDs.
 * Each font is represented with its UID and optional fileId for backend URL.
 * 
 * @param textRuns - Array of text runs, each containing font information
 * @returns Map of unique fonts keyed by font_uid
 */
export function extractUniqueFonts(
  textRuns: Array<{ font: { fingerprint: { font_uid: string } } }>
): Map<string, FontRequest> {
  const uniqueFonts = new Map<string, FontRequest>();
  
  for (const run of textRuns) {
    const font_uid = run.font.fingerprint.font_uid;
    if (!uniqueFonts.has(font_uid)) {
      uniqueFonts.set(font_uid, {
        font_uid,
        // Always use canonical filename with extension for backend URL
        fileId: font_uid
      });
    }
  }
  
  return uniqueFonts;
}

/**
 * Convert unique fonts map to array for FontDownloadService
 * 
 * @param uniqueFonts - Map of unique fonts
 * @returns Array of font requests
 */
export function fontsMapToArray(uniqueFonts: Map<string, FontRequest>): FontRequest[] {
  return Array.from(uniqueFonts.values());
}

